import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

// NavbarContext lets pages control the props of the shared UnifiedNavbar
// (rendered once in MainLayout) without the navbar ever unmounting.
//
// Pages call `useSetNavbarConfig({...})` once per render — typically the
// caller passes a fresh object literal, but React's setState bails out
// when the new value is referentially equal to the old, so we cache the
// flattened config in a ref and only call setConfig when something has
// actually changed (compared by JSON-serialisable fields + identity for
// non-serialisable fields like ReactNodes / functions).
//
// On unmount the hook resets the navbar back to its default empty config
// so a stale page title doesn't bleed into the next route.

const NavbarContext = createContext({ config: {}, setConfig: () => {} });

export function NavbarProvider({ children }) {
  const [config, setConfig] = useState({});
  const value = useMemo(() => ({ config, setConfig }), [config]);
  return <NavbarContext.Provider value={value}>{children}</NavbarContext.Provider>;
}

export function useSetNavbarConfig(cfg) {
  const { setConfig } = useContext(NavbarContext);
  // Build a memoizable key out of the cfg's fields. Strings/numbers are
  // serialised inline; functions and ReactNodes contribute their identity
  // (we read it via WeakMap so the same function/ReactNode keeps the same
  // synthetic id across renders and we don't churn).
  //
  // CALLERS: stabilise any function/ReactNode you pass in (useCallback /
  // useMemo). Inline `() => navigate('/x')` literals mint a new identity
  // every render — combined with the apply-on-mount + reset-on-unmount
  // pattern below that produced an "exceeded max update depth" loop.
  const keyParts = [];
  for (const k of Object.keys(cfg || {}).sort()) {
    const v = cfg[k];
    if (v === null || v === undefined) keyParts.push(`${k}=~`);
    else if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')
      keyParts.push(`${k}=${String(v)}`);
    else keyParts.push(`${k}=#${tagIdentity(v)}`);
  }
  const key = keyParts.join('|');

  // Hold the latest cfg in a ref so the apply-effect always reads the
  // freshest value but only re-runs when `key` actually changes.
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;

  // Apply the latest config whenever the structural key shifts.
  useEffect(() => {
    setConfig(cfgRef.current || {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Reset the navbar ONLY on real unmount (i.e. the page leaves the
  // tree). Splitting this off from the apply-effect avoids the previous
  // "reset → apply → reset → apply" thrash on every key change, which
  // could otherwise mask itself as a max-update-depth loop when a caller
  // forgot to stabilise a function prop.
  useEffect(() => () => setConfig({}), [setConfig]);
}

export function useNavbarConfig() {
  return useContext(NavbarContext).config;
}

// ─── synthetic-id tagger for non-serialisable values ───
const _ids = new WeakMap();
let _nextId = 0;
function tagIdentity(v) {
  if (v === null || typeof v !== 'object' && typeof v !== 'function') return String(v);
  let id = _ids.get(v);
  if (id === undefined) {
    id = ++_nextId;
    _ids.set(v, id);
  }
  return id;
}
